import { useState, useRef, useEffect, useCallback, Suspense, lazy } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraSlash } from "@fortawesome/pro-solid-svg-icons/faCameraSlash";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { cn } from "@wondermaps/ui";
import { useTranslation } from "react-i18next";
import { CloudinaryImage } from "../../ui/CloudinaryImage";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";
import type { POIPhoto } from "@wondermaps/db";

const PhotoLightbox = lazy(() => import("./PhotoLightbox").then(m => ({ default: m.PhotoLightbox })));

interface PhotoCarouselProps {
  photos: POIPhoto[];
  /** Override image height class (default: "h-56 sm:h-72") */
  heightClass?: string;
  /** Called when lightbox opens/closes — parent can block drawer dismiss */
  onLightboxChange?: (open: boolean) => void;
}

/**
 * Swipeable photo carousel for visitor POI detail view.
 * Uses CSS scroll-snap for native touch gestures.
 * Returns null if no photos.
 */
export function PhotoCarousel({ photos, heightClass = "h-56 sm:h-72", onLightboxChange }: PhotoCarouselProps) {
  const { t } = useTranslation("pois");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isOffline } = useNetworkStatus();

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    onLightboxChange?.(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    onLightboxChange?.(false);
  };

  const handleImageError = (photoId: string) => {
    setFailedImages((prev) => new Set(prev).add(photoId));
  };

  const handleImageLoad = (photoId: string) => {
    setLoadedImages((prev) => {
      if (prev.has(photoId)) return prev;
      const next = new Set(prev);
      next.add(photoId);
      return next;
    });
  };

  // Sort photos by sortOrder
  const sortedPhotos = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);

  // Track scroll position to update counter
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const itemWidth = scrollRef.current.offsetWidth;
    const newIndex = Math.round(scrollLeft / itemWidth);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < sortedPhotos.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, sortedPhotos.length]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Render nothing if no photos
  if (!sortedPhotos.length) {
    return null;
  }

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      aria-label={t("photos.gallery", { count: sortedPhotos.length })}
    >
      {/* Scrollable container with snap */}
      <div
        ref={scrollRef}
        className={cn(
          "flex overflow-x-auto snap-x snap-mandatory",
          "scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]"
        )}
      >
        {sortedPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative flex-shrink-0 w-full snap-center cursor-zoom-in"
            role="button"
            tabIndex={0}
            aria-label={t("photos.fullscreen")}
            onClick={() => openLightbox(index)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLightbox(index); } }}
          >
            {failedImages.has(photo.id) && isOffline ? (
              <div className={cn("w-full bg-gray-200 flex items-center justify-center", heightClass)}>
                <FontAwesomeIcon icon={faCameraSlash} className="w-12 h-12 text-gray-400" />
              </div>
            ) : (
              <>
                {!loadedImages.has(photo.id) && (
                  <div
                    className={cn(
                      "absolute inset-0 bg-muted flex items-center justify-center",
                      heightClass
                    )}
                    aria-hidden="true"
                  >
                    <FontAwesomeIcon
                      icon={faImage}
                      className="h-8 w-8 text-muted-foreground/40"
                    />
                  </div>
                )}
                <CloudinaryImage
                  publicId={photo.cloudinaryPublicId}
                  version={photo.cloudinaryVersion}
                  alt={photo.altText || t("photos.indicator", { current: index + 1, total: sortedPhotos.length })}
                  sizes="100vw"
                  widths={[320, 640, 800, 1280]}
                  loading={index === 0 ? "eager" : "lazy"}
                  className={cn("w-full", heightClass)}
                  onLoad={() => handleImageLoad(photo.id)}
                  onError={() => handleImageError(photo.id)}
                />
              </>
            )}
          </div>
        ))}
      </div>

      {/* Photo counter pill */}
      {sortedPhotos.length > 1 && (
        <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded-full">
          {currentIndex + 1} / {sortedPhotos.length}
        </div>
      )}

      {/* Alt text caption — overlay on image */}
      {sortedPhotos[currentIndex]?.altText && (
        <div className="absolute bottom-3 left-3 max-w-[70%] bg-black/60 text-white text-xs px-2 py-0.5 rounded-full truncate">
          {sortedPhotos[currentIndex].altText}
        </div>
      )}

      {/* Full-screen lightbox — PhotoSwipe manages its own DOM */}
      {lightboxOpen && (
        <Suspense fallback={null}>
          <PhotoLightbox
            photos={sortedPhotos}
            open={lightboxOpen}
            index={lightboxIndex}
            onClose={closeLightbox}
          />
        </Suspense>
      )}
    </div>
  );
}
